/**
 * BaseWorkFields Component
 * Fields for Base Work trips (UK and Foreign)
 */
import React from 'react';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { Briefcase, MapPin, Calendar } from 'lucide-react';

const BaseWorkFields = ({ formData, setFormData, isForeign }) => {
  return (
    <div className="space-y-4">
      {/* Info Banner */}
      {isForeign ? (
        <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
          <p className="text-sm text-slate-400">
            <span className="font-medium text-amber-400">Foreign Base Work:</span> Record work at a base abroad (e.g., office, yard, warehouse). These days count towards your qualifying days for SED.
          </p>
        </div>
      ) : (
        <div className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
          <p className="text-sm text-slate-400">
            <span className="font-medium text-slate-300">UK Base Work:</span> Record work at a UK base. These days do not count towards qualifying days for SED, but you can still track your earnings.
          </p>
        </div>
      )}

      {/* Section Header */}
      <div className="flex items-center gap-2 text-amber-400">
        <Briefcase className="h-4 w-4" />
        <span className="font-medium">Base Work Details</span>
      </div>

      {/* Base Location */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Base/Office Name</Label>
          <Input
            placeholder="e.g., Aberdeen Office"
            value={formData.base_name || ''}
            onChange={(e) => setFormData({ ...formData, base_name: e.target.value })}
            className="bg-slate-900 border-slate-600 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">{isForeign ? 'City/Country' : 'City'}</Label>
          <Input
            placeholder={isForeign ? "e.g., Rotterdam, Netherlands" : "e.g., Aberdeen"}
            value={formData.base_location || ''}
            onChange={(e) => setFormData({ ...formData, base_location: e.target.value })}
            className="bg-slate-900 border-slate-600 text-white"
          />
        </div>
      </div>

      {/* Contractor/Employer */}
      <div className="space-y-2">
        <Label className="text-slate-300">Contractor/Employer</Label>
        <Input
          placeholder="e.g., Maritime Services Ltd"
          value={formData.contractor || ''}
          onChange={(e) => setFormData({ ...formData, contractor: e.target.value })}
          className="bg-slate-900 border-slate-600 text-white"
        />
      </div>

      {/* Date Section Header */}
      <div className="flex items-center gap-2 text-blue-400 pt-2">
        <Calendar className="h-4 w-4" />
        <span className="font-medium">Work Period</span>
      </div>

      {isForeign ? (
        <>
          {/* Foreign Base: UK Travel Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Date Left UK *</Label>
              <Input
                type="date"
                value={formData.date_left_uk || ''}
                onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
                disabled={formData.no_uk_departure}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Date Returned UK *</Label>
              <Input
                type="date"
                value={formData.date_returned_uk || ''}
                onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
                disabled={formData.no_uk_return}
              />
            </div>
          </div>

          {/* Continuous Trip Checkboxes */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="no_uk_departure"
                checked={formData.no_uk_departure || false}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  no_uk_departure: checked,
                  date_left_uk: checked ? '' : formData.date_left_uk
                })}
              />
              <Label htmlFor="no_uk_departure" className="text-slate-300 text-sm cursor-pointer">
                Already abroad (didn't leave UK for this)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="no_uk_return"
                checked={formData.no_uk_return || false}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  no_uk_return: checked,
                  date_returned_uk: checked ? '' : formData.date_returned_uk
                })}
              />
              <Label htmlFor="no_uk_return" className="text-slate-300 text-sm cursor-pointer">
                Stayed abroad (didn't return to UK)
              </Label>
            </div>
          </div>

          {/* Work Start/End Dates (optional for foreign) */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-2">
              <Label className="text-slate-300">Work Start Date</Label>
              <Input
                type="date"
                value={formData.date_joined_vessel || ''}
                onChange={(e) => setFormData({ ...formData, date_joined_vessel: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500">If different from UK departure</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Work End Date</Label>
              <Input
                type="date"
                value={formData.date_left_vessel || ''}
                onChange={(e) => setFormData({ ...formData, date_left_vessel: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500">If different from UK return</p>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* UK Base: Simple Start/End Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Start Date *</Label>
              <Input
                type="date"
                value={formData.date_left_uk || ''}
                onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">End Date *</Label>
              <Input
                type="date"
                value={formData.date_returned_uk || ''}
                onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BaseWorkFields;
